import {
  AnimationClip,
  Matrix4,
  PropertyBinding,
  Quaternion,
  QuaternionKeyframeTrack,
  Vector3,
  VectorKeyframeTrack,
} from 'three';
import { boneNameIndex, normalizeBoneName } from './boneNames.js';

/**
 * Rewrites an animation clip authored for one Mixamo skeleton so it drives
 * another. This is name-based retargeting, which is what Mixamo clips want:
 * the bone hierarchy is identical, only the names and the limb lengths differ.
 *
 * Rotations transfer as-is. Translations do not: a child bone's position is its
 * bone length, so copying it from a taller rig dislocates the limb. Only the
 * root's translation survives, scaled by the height ratio between the rigs, so
 * a step stays a step rather than becoming a stride.
 *
 * `yaw` turns the whole character, exactly as it does in `retargetPosedClip`:
 * the root's rotation is premultiplied by it and the root's translation turned
 * with it. A clip with no root rotation track gets one, holding the turn —
 * without it the mixer would leave the root at its bind pose, facing whichever
 * way the bind pose happens to face.
 */
export function retargetClip(clip, targetSkeleton, { sourceSkeleton, yaw = 0, onWarn } = {}) {
  const index = boneNameIndex(targetSkeleton.bones);
  const rootName = normalizeBoneName(targetSkeleton.bones[0]?.name ?? '');
  const scale = hipScale(targetSkeleton, sourceSkeleton, clip);

  const tracks = [];
  const unmapped = new Set();
  const spin = new Quaternion().setFromAxisAngle(UP, yaw);
  const rootBoneName = targetSkeleton.bones[0]?.name;
  let rootTurned = false;

  for (const track of clip.tracks) {
    const parsed = PropertyBinding.parseTrackName(track.name);
    const key = normalizeBoneName(parsed.nodeName);
    const targetName = index.get(key);

    if (!targetName) {
      unmapped.add(parsed.nodeName);
      continue;
    }

    if (parsed.propertyName === 'quaternion') {
      const copy = renamed(track, `${targetName}.quaternion`);
      if (yaw && key === rootName) {
        turnQuaternions(copy.values, spin);
        rootTurned = true;
      }
      tracks.push(copy);
    } else if (parsed.propertyName === 'position' && key === rootName) {
      const scaled = renamed(track, `${targetName}.position`);
      for (let i = 0; i < scaled.values.length; i++) scaled.values[i] *= scale;
      if (yaw) turnVectors(scaled.values, spin);
      tracks.push(scaled);
    }
    // Everything else — limb translations, scale tracks — is dropped.
  }

  if (yaw && !rootTurned && rootBoneName) {
    const held = new Quaternion().copy(targetSkeleton.bones[0].quaternion).premultiply(spin);
    tracks.push(new QuaternionKeyframeTrack(`${rootBoneName}.quaternion`, [0], held.toArray()));
  }

  if (unmapped.size && onWarn) {
    onWarn(`retarget "${clip.name}": no target bone for ${[...unmapped].join(', ')}`);
  }

  return new AnimationClip(clip.name, clip.duration, tracks, clip.blendMode);
}

const TURN = /* @__PURE__ */ new Quaternion();
const POINT = /* @__PURE__ */ new Vector3();

function turnQuaternions(values, spin) {
  for (let i = 0; i < values.length; i += 4) {
    TURN.fromArray(values, i).premultiply(spin).toArray(values, i);
  }
}

function turnVectors(values, spin) {
  for (let i = 0; i < values.length; i += 3) {
    POINT.fromArray(values, i).applyQuaternion(spin).toArray(values, i);
  }
}

/** Retargets every clip in a list, keeping the originals untouched. */
export function retargetClips(clips, targetSkeleton, options) {
  return clips.map((clip) => retargetClip(clip, targetSkeleton, options));
}

function renamed(track, name) {
  const copy = track.clone();
  copy.name = name;
  return copy;
}

/**
 * Height ratio between the rigs. A source skeleton gives the exact answer; with
 * only a clip to go on, the root track's first value is the source's rest hip
 * height, which is close enough for placeholder work.
 */
function hipScale(targetSkeleton, sourceSkeleton, clip) {
  const targetHeight = targetSkeleton.bones[0]?.position.y ?? 0;
  if (!targetHeight) return 1;

  const sourceHeight = sourceSkeleton
    ? sourceSkeleton.bones[0]?.position.y
    : rootTrackFirstY(clip);

  if (!sourceHeight) return 1;
  return targetHeight / sourceHeight;
}

function rootTrackFirstY(clip) {
  let fallback = 0;
  for (const track of clip.tracks) {
    const parsed = PropertyBinding.parseTrackName(track.name);
    if (parsed.propertyName !== 'position') continue;
    if (normalizeBoneName(parsed.nodeName) === 'hips') return track.values[1];
    if (!fallback) fallback = track.values[1];
  }
  return fallback;
}

// --- rest-pose retargeting -------------------------------------------------

/**
 * Retargets a clip whose source rig does not share the target's rest pose.
 *
 * `retargetClip` above copies local rotations across by name, which is only
 * correct while both rigs agree on where a bone's local axes point. Real
 * Mixamo rigs do not agree with the placeholder: every Mixamo bone runs down
 * its own local +Y, while the generated mannequin's bones sit at identity and
 * run down whichever world axis the T-pose put them on. Copying a local
 * quaternion between those two puts the arm 90 degrees off.
 *
 * So this path works in world space instead. For every bone it takes the
 * rotation the source has moved *away from its own rest*, and applies that
 * same world rotation to the target's rest:
 *
 *     worldTarget(b) = worldSource(b) · rest(b)          rest(b) = restSourceᵀ · restTarget
 *
 * which leaves rest mapping to rest and any real motion surviving intact. The
 * hierarchy has to be evaluated frame by frame to do it, so the clip is
 * resampled rather than rewritten track by track.
 *
 * `yaw` turns the whole character: Mixamo authors facing +Z and this rig's
 * forward is its own -Z, so a half turn here is what stops every clip playing
 * backwards.
 *
 * The source's *rest* pose is read straight off the bones, so this has to run
 * on a freshly loaded rig — pose it first and the rest it compensates against
 * is whatever frame the mixer left behind, which collapses every clip to the
 * target's T-pose.
 *
 * `rootMotion` decides what happens to the hips' translation:
 *   `offset`   keep the motion, but start it where the fighter is standing.
 *              A taunt captured 78cm off the origin should not teleport.
 *   `inPlace`  drop the horizontal travel entirely and keep only the height.
 *              The game owns where a fighter is; a walk cycle does not.
 *   `keep`     take the source's translation as-is, scaled.
 */
export function retargetPosedClip(
  clip,
  targetSkeleton,
  { sourceRoot, name = clip.name, yaw = 0, rootMotion = 'offset', onWarn } = {}
) {
  const sourceBones = collectBones(sourceRoot);
  if (!sourceBones.length) {
    onWarn?.(`retarget "${clip.name}": source has no bones`);
    return new AnimationClip(name, clip.duration, []);
  }

  const sourceIndex = new Map();
  for (const bone of sourceBones) {
    const key = normalizeBoneName(bone.name);
    if (key && !sourceIndex.has(key)) sourceIndex.set(key, bone);
  }

  const sourceOrder = new Map(sourceBones.map((bone, i) => [bone, i]));
  const sourceRest = restWorldRotations(sourceBones, sourceOrder);
  const targetRest = targetSkeleton.bones.map((_, i) => bindRotation(targetSkeleton, i));
  const targetOrder = new Map(targetSkeleton.bones.map((bone, i) => [bone, i]));

  const unmapped = [];
  const plan = targetSkeleton.bones.map((bone, i) => {
    const source = sourceIndex.get(normalizeBoneName(bone.name)) ?? null;
    if (!source) unmapped.push(bone.name);
    return {
      bone,
      source: source ? sourceOrder.get(source) : -1,
      parent: targetOrder.has(bone.parent) ? targetOrder.get(bone.parent) : -1,
      // The constant that carries a source bone's rest frame onto the target's.
      rest: source
        ? new Quaternion().copy(sourceRest[sourceOrder.get(source)]).invert().multiply(targetRest[i])
        : null,
      world: new Quaternion(),
      values: [],
    };
  });

  if (unmapped.length) {
    onWarn?.(`retarget "${name}": no source bone for ${unmapped.join(', ')}`);
  }

  const times = sampleTimes(clip);
  const rotations = quaternionInterpolants(clip, sourceBones, sourceOrder);
  const scale = boneScale(targetSkeleton, sourceBones[0]);
  const spin = new Quaternion().setFromAxisAngle(UP, yaw);

  const local = new Quaternion();
  const inverse = new Quaternion();
  const worlds = sourceBones.map(() => new Quaternion());
  const rootTrack = rootPositionTrack(clip, sourceBones[0]);
  const rootValues = [];
  const point = new Vector3();
  const origin = new Vector3();
  const rest = bindPosition(targetSkeleton, 0);
  if (rootTrack && rootMotion === 'offset') {
    origin.fromArray(rootTrack.interpolant.evaluate(times[0])).multiplyScalar(scale);
    origin.y = 0;
  }

  for (const time of times) {
    // 1. Pose the source hierarchy: local rotation from the clip where there is
    //    a curve, the bone's own rest where there is not.
    for (let i = 0; i < sourceBones.length; i++) {
      const bone = sourceBones[i];
      const interpolant = rotations[i];
      if (interpolant) local.fromArray(interpolant.evaluate(time));
      else local.copy(bone.quaternion);

      const parent = sourceOrder.get(bone.parent);
      worlds[i].copy(local);
      if (parent !== undefined) worlds[i].premultiply(worlds[parent]);
    }

    // 2. Carry each world rotation onto the target rig's rest frame, then read
    //    it back out as a local rotation against the target's own parent.
    for (const entry of plan) {
      if (entry.source === -1) {
        entry.world.copy(targetRest[targetOrder.get(entry.bone)]);
      } else {
        entry.world.copy(worlds[entry.source]).multiply(entry.rest);
      }

      if (entry.parent === -1) local.copy(entry.world).premultiply(spin);
      else local.copy(entry.world).premultiply(inverse.copy(plan[entry.parent].world).invert());

      entry.values.push(local.x, local.y, local.z, local.w);
    }

    if (rootTrack) {
      point.fromArray(rootTrack.interpolant.evaluate(time)).multiplyScalar(scale);
      if (rootMotion === 'inPlace') {
        point.x = rest.x;
        point.z = rest.z;
      } else if (rootMotion === 'offset') {
        point.sub(origin);
      }
      rootValues.push(...point.applyQuaternion(spin).toArray());
    }
  }

  const tracks = plan.map(
    (entry) => new QuaternionKeyframeTrack(`${entry.bone.name}.quaternion`, times, entry.values)
  );
  if (rootTrack) {
    tracks.push(new VectorKeyframeTrack(`${plan[0].bone.name}.position`, times, rootValues));
  }

  return new AnimationClip(name, clip.duration, tracks);
}

const UP = /* @__PURE__ */ new Vector3(0, 1, 0);

/** Every Bone under an object, parents before children. */
function collectBones(root) {
  const bones = [];
  root?.traverse?.((child) => {
    if (child.isBone) bones.push(child);
  });
  return bones;
}

/** World rotation of each bone in its rest pose, accumulated down the tree. */
function restWorldRotations(bones, order) {
  const rest = bones.map(() => new Quaternion());
  for (let i = 0; i < bones.length; i++) {
    rest[i].copy(bones[i].quaternion);
    const parent = order.get(bones[i].parent);
    if (parent !== undefined) rest[i].premultiply(rest[parent]);
  }
  return rest;
}

const MATRIX = /* @__PURE__ */ new Matrix4();
const SCRATCH_POSITION = /* @__PURE__ */ new Vector3();
const SCRATCH_SCALE = /* @__PURE__ */ new Vector3();

/**
 * A target bone's bind-pose world rotation. `boneInverses` is the only record
 * of the rest pose that survives once the mixer starts posing the skeleton,
 * which is why it is read from there rather than from the live bones.
 */
function bindRotation(skeleton, i) {
  const quaternion = new Quaternion();
  MATRIX.copy(skeleton.boneInverses[i]).invert();
  MATRIX.decompose(SCRATCH_POSITION, quaternion, SCRATCH_SCALE);
  return quaternion;
}

function bindPosition(skeleton, i) {
  const position = new Vector3();
  MATRIX.copy(skeleton.boneInverses[i]).invert();
  MATRIX.decompose(position, new Quaternion(), SCRATCH_SCALE);
  return position;
}

/**
 * Metres per source unit. Mixamo exports centimetres, so this is doing two
 * jobs at once: the unit change and the height difference between the rigs.
 */
function boneScale(targetSkeleton, sourceRoot) {
  const target = bindPosition(targetSkeleton, 0).y;
  const source = sourceRoot?.position.y ?? 0;
  if (!target || !source) return 1;
  return target / source;
}

/** Every distinct keyframe time in a clip, in order. */
function sampleTimes(clip) {
  const seen = new Set();
  for (const track of clip.tracks) for (const time of track.times) seen.add(time);
  const times = [...seen].sort((a, b) => a - b);
  return times.length ? times : [0];
}

/** One interpolant per source bone, indexed alongside `bones`. */
function quaternionInterpolants(clip, bones, order) {
  const interpolants = bones.map(() => null);
  const index = new Map();
  for (const bone of bones) {
    const key = normalizeBoneName(bone.name);
    if (key && !index.has(key)) index.set(key, bone);
  }

  for (const track of clip.tracks) {
    const parsed = PropertyBinding.parseTrackName(track.name);
    if (parsed.propertyName !== 'quaternion') continue;
    const bone = index.get(normalizeBoneName(parsed.nodeName));
    if (bone) interpolants[order.get(bone)] = track.createInterpolant();
  }
  return interpolants;
}

function rootPositionTrack(clip, rootBone) {
  const key = normalizeBoneName(rootBone.name);
  for (const track of clip.tracks) {
    const parsed = PropertyBinding.parseTrackName(track.name);
    if (parsed.propertyName !== 'position') continue;
    if (normalizeBoneName(parsed.nodeName) !== key) continue;
    return { track, interpolant: track.createInterpolant() };
  }
  return null;
}
